<template>
  <div class="w-full h-full min-h-0">
    <div class="w-full h-full min-h-0">
      <!-- Use generic Chart for barWithErrorBars -->
      <GenericChart
        v-if="chartConfig && hasErrorBars"
        type="barWithErrorBars"
        :data="getTranslatedChartData(chartConfig)"
        :options="chartOptions"
        :plugins="chartPlugins"
        class="w-full h-full"
      />
      <component
        v-else-if="chartConfig && chartConfig.type"
        :is="chartConfig.type"
        :data="getTranslatedChartData(chartConfig)"
        :options="chartOptions"
        :plugins="chartPlugins"
        class="w-full h-full"
      />
      <div
        v-else
        class="flex items-center justify-center h-full text-gray-500"
      >
        {{ $t('BarChartComponent.noDataAvailable') || 'No data available' }}
      </div>
    </div>
  </div>
</template>

<script>
import { Bar, Line, Chart as GenericChart } from 'vue-chartjs';
import 'chartjs-adapter-date-fns';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
  TimeScale,
  Filler,
} from 'chart.js';
import {
  BarWithErrorBarsController,
  BarWithErrorBar,
} from 'chartjs-chart-error-bars';
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
  TimeScale,
  Filler,
  BarWithErrorBarsController,
  BarWithErrorBar,
);

/** When y is 0 we use this value so a small bar is visible; tooltip still shows actual 0. */
const VISUAL_ZERO = 0.2;

export default {
  name: 'BarChartComponent',
  components: {
    Bar,
    Line,
    GenericChart,
  },
  props: {
    chartConfig: {
      type: Object,
      required: true,
    },
    dimensions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      windowWidth: window.innerWidth,
    };
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  computed: {
    isMobile() {
      return this.windowWidth < 768;
    },
    useLikertScale() {
      const y = this.chartConfig?.options?.scales?.y;
      if (!y || y.min !== 0) return false;
      const max = y.max ?? y.suggestedMax;
      return max === 5 || max === 6;
    },
    /** Bar / bar+error bars on Likert: 0–4 plot, y-axis −1…5 like line chart */
    useFloatingLikertBars() {
      if (!this.useLikertScale) return false;
      return this.chartConfig?.type === 'bar' || this.hasErrorBars;
    },
    showMobileLikertLegend() {
      return this.isMobile && this.useLikertScale;
    },
    /**
     * Check if any dataset in the chart config uses error bar data format
     * (objects with y, yMin, yMax instead of plain numbers)
     */
    hasErrorBars() {
      return this.chartConfig?.data?.datasets?.some(
        (ds) => ds.data?.some(
          (d) => d !== null
            && typeof d === 'object'
            && 'yMin' in d,
        ),
      );
    },

    /**
     * Whether the chart is in comparison mode (2+ datasets)
     */
    hasComparison() {
      const ds = this.chartConfig?.data?.datasets;
      return ds && ds.length >= 2;
    },

    /**
     * Extra top padding to fit data labels above bars.
     * Stack: [% diff] → [CV badge] → [mean ±SD] → whisker → bar
     */
    labelTopPadding() {
      if (this.hasComparison && this.hasErrorBars) return 58;
      if (this.hasErrorBars) return 44;
      if (this.hasComparison) return 32;
      // Single-series bar: value labels above bars need headroom
      return 32;
    },

    /**
     * Chart.js inline plugins – ALWAYS registered so they survive
     * reactive chart updates (Chart.js does not hot-swap plugins).
     * Each plugin checks conditions at draw-time.
     *
     *  1) barValueLabels  – mean ±SD on each bar
     *  2) cvIndicator     – CV badge when CV ≥ 20 %
     *  3) comparisonPct   – % diff between comparison bar pairs
     */
    chartPlugins() {
      const { isMobile } = this;

      // --- helpers (shared by all plugins via closure) ---
      const getVal = (d) => {
        if (d == null) return null;
        if (typeof d === 'object' && 'y' in d) return d.y;
        if (typeof d === 'number') return d;
        return null;
      };
      const getSd = (d) => {
        if (d != null && typeof d === 'object' && 'yMax' in d) {
          return d.yMax - d.y;
        }
        return 0;
      };
      const topOf = (dp, bar, yScale) => {
        const sd = getSd(dp);
        return sd > 0
          ? yScale.getPixelForValue(dp.yMax)
          : bar.y;
      };
      const hasEb = (chart) => chart.data.datasets.some(
        (ds) => ds.data?.some(
          (d) => d != null
            && typeof d === 'object'
            && 'yMin' in d,
        ),
      );

      /** Match dataset bar fill (or stroke) for value label text */
      const pickBarColor = (ds, barIndex) => {
        const from = (c) => {
          if (c == null) return null;
          if (Array.isArray(c)) {
            const x = c[barIndex] ?? c[0];
            return typeof x === 'string' ? x : null;
          }
          if (typeof c === 'string') return c;
          return null;
        };
        return from(ds.backgroundColor) || from(ds.borderColor) || '#4b5563';
      };

      // CV thresholds (in %)
      const CV_MOD = 20;
      const CV_HIGH = 35;

      /** Match ResultsInsightsPanel / comparison delta font colors */
      const CHANGE_PCT_POSITIVE = '#e4821a';
      const CHANGE_PCT_NEGATIVE = '#e44b55';
      const CHANGE_PCT_NEUTRAL = '#475569';

      return [
        // --------------------------------------------------
        // Plugin 1: Value labels (mean ±SD) on every bar
        // Always for bar charts (mean ±SD when error bars exist)
        // --------------------------------------------------
        {
          id: 'barValueLabels',
          afterDatasetsDraw(chart) {
            const { ctx } = chart;
            const yScale = chart.scales.y;
            if (!yScale) return;
            const fs = isMobile ? 7 : 9;
            const floatLikert = yScale.min === -1 && yScale.max === 5;
            const likertDisplayMean = (v) => {
              if (v == null || Number.isNaN(v)) return v;
              if (!floatLikert) return v;
              if (v > 0 && v <= 0.35) return 1;
              return v + 1;
            };

            chart.data.datasets.forEach((ds, dsIdx) => {
              const meta = chart.getDatasetMeta(dsIdx);
              if (meta.hidden) return;

              ds.data.forEach((dp, i) => {
                const bar = meta.data[i];
                if (!bar) return;
                const val = getVal(dp);
                if (val == null) return;

                const sd = getSd(dp);
                const meanShown = likertDisplayMean(val);
                let label = meanShown.toFixed(2);
                if (sd > 0.01) {
                  label = `${label} \u00B1${sd.toFixed(2)}`;
                }

                const top = topOf(dp, bar, yScale);
                ctx.save();
                ctx.textAlign = 'center';
                ctx.textBaseline = 'bottom';
                ctx.font = `${fs}px sans-serif`;
                ctx.fillStyle = pickBarColor(ds, i);
                ctx.fillText(label, bar.x, top - 3);
                ctx.restore();
              });
            });
          },
        },

        // --------------------------------------------------
        // Plugin 2: CV indicator badge on error-bar bars
        // Only drawn when error-bar data exists & CV ≥ 20 %
        // --------------------------------------------------
        {
          id: 'cvIndicator',
          afterDatasetsDraw(chart) {
            if (!hasEb(chart)) return;

            const { ctx } = chart;
            const yScale = chart.scales.y;
            if (!yScale) return;
            const fs = isMobile ? 6 : 8;
            const pad = 2;
            const floatLikert = yScale.min === -1 && yScale.max === 5;
            const likertMeanForCv = (v) => {
              if (v == null) return 0;
              if (!floatLikert) return v;
              if (v > 0 && v <= 0.35) return 1;
              return v + 1;
            };

            chart.data.datasets.forEach((ds, dsIdx) => {
              const meta = chart.getDatasetMeta(dsIdx);
              if (meta.hidden) return;

              ds.data.forEach((dp, i) => {
                const sd = getSd(dp);
                if (sd < 0.01) return;
                const val = getVal(dp);
                const meanL = likertMeanForCv(val);
                if (!meanL || meanL <= 0) return;

                const cv = (sd / meanL) * 100;
                if (cv < CV_MOD) return;

                const bar = meta.data[i];
                if (!bar) return;
                const top = topOf(dp, bar, yScale);

                const text = `CV ${cv.toFixed(2)}%`;
                const baseFs = isMobile ? 7 : 9;
                const yPos = top - baseFs - 6;

                ctx.save();
                ctx.font = `bold ${fs}px sans-serif`;
                const tw = ctx.measureText(text).width;
                const bw = tw + pad * 2 + 4;
                const bh = fs + pad * 2 + 1;
                const bx = bar.x - bw / 2;
                const by = yPos - bh;

                const isHigh = cv >= CV_HIGH;
                ctx.fillStyle = isHigh
                  ? 'rgba(239, 68, 68, 0.12)'
                  : 'rgba(245, 158, 11, 0.12)';
                ctx.beginPath();
                ctx.roundRect(bx, by, bw, bh, 3);
                ctx.fill();

                ctx.textAlign = 'center';
                ctx.textBaseline = 'bottom';
                ctx.fillStyle = isHigh
                  ? '#dc2626'
                  : '#d97706';
                ctx.fillText(text, bar.x, yPos - 1);
                ctx.restore();
              });
            });
          },
        },

        // --------------------------------------------------
        // Plugin 3: Percentage diff between bar pairs
        // Active when chart has 2+ visible datasets
        // --------------------------------------------------
        {
          id: 'comparisonPercentLabels',
          afterDatasetsDraw(chart) {
            const dsList = chart.data.datasets;
            if (dsList.length < 2) return;

            const { ctx } = chart;
            const yScale = chart.scales.y;
            if (!yScale) return;
            const fs = isMobile ? 7 : 9;
            const meta0 = chart.getDatasetMeta(0);
            const meta1 = chart.getDatasetMeta(1);
            if (meta0.hidden || meta1.hidden) return;

            const errorBars = hasEb(chart);
            const floatLikert = yScale.min === -1 && yScale.max === 5;
            const toLikert5 = (v) => {
              if (v == null || Number.isNaN(v)) return v;
              if (!floatLikert) return v;
              if (v > 0 && v <= 0.35) return 1;
              return v + 1;
            };
            const len = Math.min(
              dsList[0].data.length,
              dsList[1].data.length,
            );

            for (let i = 0; i < len; i += 1) {
              const v0 = getVal(dsList[0].data[i]);
              const v1 = getVal(dsList[1].data[i]);
              /* eslint-disable no-continue */
              if (v0 == null || v1 == null) continue;
              // Dataset 0 = left bar, dataset 1 = right bar (compare dropdowns).
              // Positive % = right higher than left; negative = right lower than left.
              const wLeft = toLikert5(v0);
              const wRight = toLikert5(v1);
              if (wLeft === 0 && wRight === 0) continue;

              let pctText;
              let pct = 0;
              if (!wLeft || wLeft === 0) {
                pctText = 'N/A';
              } else {
                pct = ((wRight - wLeft) / Math.abs(wLeft)) * 100;
                if (Math.abs(pct) < 0.5) continue;
                pctText = pct > 0
                  ? `\u2191+${pct.toFixed(2)}%`
                  : `\u2193${pct.toFixed(2)}%`;
              }
              /* eslint-enable no-continue */

              const bar0 = meta0.data[i];
              const bar1 = meta1.data[i];
              if (!bar0 || !bar1) continue; // eslint-disable-line no-continue

              const top0 = topOf(dsList[0].data[i], bar0, yScale);
              const top1 = topOf(dsList[1].data[i], bar1, yScale);

              const x = (bar0.x + bar1.x) / 2;
              const topY = Math.min(top0, top1);
              const clearance = errorBars
                ? (2 * fs + 18)
                : (fs + 12);
              const y = topY - clearance;

              ctx.save();
              ctx.textAlign = 'center';
              ctx.textBaseline = 'bottom';
              ctx.font = `bold ${fs + 1}px sans-serif`;
              if (pct > 0) {
                ctx.fillStyle = CHANGE_PCT_POSITIVE;
              } else if (pct < 0) {
                ctx.fillStyle = CHANGE_PCT_NEGATIVE;
              } else {
                ctx.fillStyle = CHANGE_PCT_NEUTRAL;
              }
              ctx.fillText(pctText, x, y);
              ctx.restore();
            }
          },
        },
      ];
    },
    chartOptions() {
      return {
        ...this.chartConfig.options,
        plugins: {
          ...this.chartConfig.options?.plugins,
          legend: {
            display: true,
            position: this.isMobile ? 'bottom' : 'top',
            labels: {
              boxWidth: this.isMobile ? 12 : 15,
              padding: this.isMobile ? 8 : 12,
              font: {
                size: this.isMobile ? 10 : 12,
              },
              // Filter out hidden error-bar legend entries
              filter: (legendItem) => {
                if (legendItem.text === this.$t('TeamResultDashboardComponent.standardDeviation')) return false;
                return true;
              },
            },
          },
          tooltip: {
            enabled: true,
            callbacks: {
              title: (context) => this.getTooltipLabel(context[0]),
              label: (context) => {
                const { raw, dataset, parsed } = context;
                const datasetLabel = dataset?.label || '';
                const tooltipPrefix = this.getTooltipSeriesPrefix(datasetLabel);

                // Error bar data point: show value ± SD + CV
                if (raw && typeof raw === 'object' && 'yMin' in raw) {
                  const value = raw.y;
                  const sd = raw.yMax - raw.y;
                  const formatted = this.useLikertScale
                    ? this.formatLikertTooltipValue(value)
                    : Number(value).toFixed(2);
                  if (sd > 0) {
                    const sdFmt = sd.toFixed(2);
                    const cvPart = value > 0
                      ? ` | CV: ${((sd / value) * 100).toFixed(2)}%`
                      : '';
                    return `${tooltipPrefix}${formatted} (SD: \u00B1${sdFmt}${cvPart})`;
                  }
                  return `${tooltipPrefix}${formatted}`;
                }

               // Standard numeric value
               const value = parsed?.y;
                if (this.useLikertScale) {
                  const formatted = this.formatLikertTooltipValue(value);
                  return `${tooltipPrefix}${formatted}`;
                }
                const formatted = value != null && !Number.isNaN(Number(value))
                  ? Number(value).toFixed(2)
                  : value;
                return `${tooltipPrefix}${formatted}`;
              },
              labelColor: (context) => {
                // Check if this is the Standard Deviation dataset
                const datasetLabel = context.dataset.label || '';
                if (datasetLabel === 'Standard Deviation') {
                  return {
                    borderColor: '#000000',
                    backgroundColor: '#000000',
                  };
                }
                // Return default colors for other datasets
                return {
                  borderColor: context.dataset.borderColor || context.dataset.backgroundColor,
                  backgroundColor: context.dataset.backgroundColor || context.dataset.borderColor,
                };
              },
            },
          },
        },
        maintainAspectRatio: false,
        responsive: true,
        scales: {
          ...this.chartConfig.options?.scales,
          x: {
            ...this.chartConfig.options?.scales?.x,
            ticks: {
              ...this.chartConfig.options?.scales?.x?.ticks,
              maxRotation: 0,
              minRotation: 0,
              font: {
                size: this.isMobile ? 8 : 11,
                lineHeight: this.isMobile ? 1.2 : 1.4,
              },
              maxTicksLimit: this.isMobile ? 6 : 10,
              callback: (value, index) => {
                // Get the translated label from the chart data using index
                const translatedData = this.getTranslatedChartData(this.chartConfig);
                const label = translatedData.labels[index];
                if (!label) return '';
                // Use formatLabel with appropriate maxwidth based on screen size
                const maxwidth = 10;
                return this.formatLabel(String(label), maxwidth);
              },
            },
            grid: {
              ...this.chartConfig.options?.scales?.x?.grid,
              display: !this.isMobile,
            },
          },
          y: {
            ...this.chartConfig.options?.scales?.y,
            // Scale from -1, max 5: 0 = Strongly disagree, bars fly above axis
            ...(this.useFloatingLikertBars ? { min: -1, max: 5 } : {}),
            ticks: {
              ...this.chartConfig.options?.scales?.y?.ticks,
              font: {
                size: this.isMobile ? 9 : 11,
              },
              callback: this.useLikertScale
                ? (value) => {
                  const v = Number(value);
                  if (v === -1) return '';
                  // Mobile + Likert: show only numbers 1–5 on y-axis (legend below explains scale)
                  if (this.showMobileLikertLegend) {
                    if (this.useFloatingLikertBars && v >= 0 && v <= 4 && Number.isInteger(v)) {
                      return String(v + 1);
                    }
                    // Non-floating 1–5 scale: show 1–5; avoid showing 5 twice when scale max is 5
                    if (v >= 1 && v <= 4 && Number.isInteger(v)) return String(v);
                    if (v === 5 && Number.isInteger(v) && !this.useFloatingLikertBars) return '5';
                    return '';
                  }
                  if (this.useFloatingLikertBars) {
                    if (v >= 0 && v <= 4 && Number.isInteger(v)) return this.getLikertYAxisLabel(v);
                  } else if (v >= 1 && v <= 5 && Number.isInteger(v)) {
                    return this.getLikertYAxisLabel(v);
                  }
                  return '';
                }
                : this.chartConfig.options?.scales?.y?.ticks?.callback,
            },
          },
        },
        interaction: {
          intersect: false,
          mode: 'index',
        },
        layout: {
          padding: {
            top: this.labelTopPadding,
            bottom: this.isMobile ? 20 : 10,
          },
        },
        elements: {
          bar: {
            borderWidth: this.isMobile ? 1 : 2,
          },
        },
      };
    },
  },
  methods: {
    /**
     * Handle window resize to update mobile detection
     */
    handleResize() {
      this.windowWidth = window.innerWidth;
    },

    /**
     * Format label into sections based on maxwidth
     * @param {string} str - The string to format
     * @param {number} maxwidth - Maximum width per line
     * @returns {string[]} Array of string sections for multi-line display
     */
    formatLabel(str, maxwidth) {
      const sections = [];
      const words = str.split(' ');
      let temp = '';

      words.forEach((item, index) => {
        if (temp.length > 0) {
          const concat = temp.concat(' ').concat(item);

          if (concat.length > maxwidth) {
            sections.push(temp);
            temp = '';
          } else {
            if (index === (words.length - 1)) {
              sections.push(concat);
              return;
            }
            temp = concat;
            return;
          }
        }

        if (index === (words.length - 1)) {
          sections.push(item);
          return;
        }

        if (item.length < maxwidth) {
          temp = item;
        } else {
          sections.push(item);
        }
      });
      return sections;
    },

    /**
     * Get tooltip label - simplified approach
     * @param {Object} tooltipItem - The tooltip item from Chart.js
     * @returns {string} The correct label for the tooltip
     */
    getTooltipLabel(tooltipItem) {
      // Use dataIndex to get the original label from chartConfig
      /* if (tooltipItem.dataIndex >= 0 && this.chartConfig?.data?.labels) {
        const originalLabel = this.chartConfig.data.labels[tooltipItem.dataIndex];
        if (originalLabel) {
          return this.getDimensionLabel(originalLabel);
        }
      }

      // Fallback to the tooltip's label if dataIndex fails
      return tooltipItem.label || ''; */
      return this.$t('TeamResultDashboardComponent.mean');
    },

    /**
     * Extract date token from dataset legend label.
     * Supports common localized date formats used in comparison labels.
     */
    extractDateFromDatasetLabel(datasetLabel) {
      if (!datasetLabel || typeof datasetLabel !== 'string') return '';
      const text = datasetLabel.trim();
      const patterns = [
        /\b\d{4}-\d{2}-\d{2}\b/u,
        /\b\d{1,2}[./-]\d{1,2}[./-]\d{2,4}\b/u,
        /\b\d{1,2}\s+[A-Za-z]{3,}\s+\d{4}\b/u,
        /\b[A-Za-z]{3,}\s+\d{1,2},\s+\d{4}\b/u,
      ];
      for (const re of patterns) {
        const match = text.match(re);
        if (match?.[0]) return match[0];
      }
      return '';
    },

    /** Tooltip prefix prefers date; falls back to assessment name. */
    getTooltipSeriesPrefix(datasetLabel) {
      const date = this.extractDateFromDatasetLabel(datasetLabel);
      if (date) return `${date}: `;
      return datasetLabel ? `${datasetLabel}: ` : '';
    },

    /**
     * Get translated Likert label for Y-axis.
     * When useFloatingLikertBars: 0–4 (Strongly disagree → Strongly agree).
     * Otherwise 1–5 (Strongly disagree → Strongly agree).
     * @param {number} value - Integer 0–4 or 1–5 depending on chart mode
     * @returns {string} Translated label
     */
    getLikertYAxisLabel(value) {
      const keyMap04 = {
        0: 'SliderQuestion.datalistOptions.stronglyDisagree',
        1: 'SliderQuestion.datalistOptions.disagree',
        2: 'SliderQuestion.datalistOptions.indifferent',
        3: 'SliderQuestion.datalistOptions.agree',
        4: 'SliderQuestion.datalistOptions.stronglyAgree',
      };
      const keyMap15 = {
        1: 'SliderQuestion.datalistOptions.stronglyDisagree',
        2: 'SliderQuestion.datalistOptions.disagree',
        3: 'SliderQuestion.datalistOptions.indifferent',
        4: 'SliderQuestion.datalistOptions.agree',
        5: 'SliderQuestion.datalistOptions.stronglyAgree',
      };
      const keyMap = this.useFloatingLikertBars ? keyMap04 : keyMap15;
      const key = keyMap[value];
      return key ? this.$t(key) : String(value);
    },

    /**
     * Format numeric value for tooltip: show number and Likert label
     * (e.g. "4 (Agree)" or "3.5 (Between Indifferent and Agree)")
     * When useFloatingLikertBars, value is 0–4; otherwise 1–5.
     * @param {number} value - Raw value (may be decimal)
     * @returns {string} Formatted string
     */
    formatLikertTooltipValue(value) {
      if (value == null || Number.isNaN(value)) return '';
      // Bar chart shows 0 as VISUAL_ZERO for visibility; treat as 0 for tooltip
      const raw = Number(value);
      const isVisualZero = raw > 0 && raw <= 0.3;
      const effective = this.useFloatingLikertBars && isVisualZero ? 0 : raw;
      const num = this.useFloatingLikertBars ? effective + 1 : effective;
      const is04 = this.useFloatingLikertBars;
      const minV = is04 ? 0 : 1;
      const maxV = is04 ? 4 : 5;
      const rounded = Math.round(num);
      const label = this.getLikertYAxisLabel(Math.min(maxV, Math.max(minV, rounded)));
      const fixed = num.toFixed(2);
      /* if (Number.isInteger(num) && num >= minV && num <= maxV) {
        return `${fixed} (${label})`;
      }
      const lower = Math.floor(Math.min(maxV, Math.max(minV, num)));
      const upper = Math.min(maxV, lower + 1);
      if (lower === upper) return `${fixed} (${label})`;
      const lowerLabel = this.getLikertYAxisLabel(lower);
      const upperLabel = this.getLikertYAxisLabel(upper);
      const between = this.$t('LineChartComponent.likertBetween', [lowerLabel, upperLabel]); */
      return `${fixed}`;
    },

    /**
     * Normalize color to ensure it has # prefix
     * Handles both formats: colors with # (from KSS dashboard) and without # (from other charts)
     * @param {string|Array} color - The color value (hex string or array of colors)
     * @returns {string|Array} The normalized color with # prefix
     */
    normalizeColor(color) {
      if (!color) return color;
      // Handle array of colors
      if (Array.isArray(color)) {
        return color.map((c) => this.normalizeColor(c));
      }
      // Handle string color - add # only for hex, leave rgb/rgba unchanged
      if (typeof color === 'string') {
        if (color.startsWith('#') || color.startsWith('rgb')) return color;
        return `#${color}`;
      }
      return color;
    },

    /**
     * Get translated dimension label for display purposes
     * @param {string} dimensionValue - The dimension value
     * @returns {string} The translated label
     */
    getDimensionLabel(dimensionValue) {
      const dimension = this.dimensions.find((d) => d.value === dimensionValue);
      const locale = this.$i18n?.locale || 'en';
      return dimension?.translation?.[locale] || dimensionValue;
    },

    /**
     * Get chart data with translated labels
     * @param {Object} chartConfig - The chart configuration
     * @returns {Object} Chart data with translated labels
     */
    getTranslatedChartData(chartConfig) {
      if (!chartConfig || !chartConfig.data) {
        return chartConfig;
      }

      const translatedLabels = chartConfig.data.labels
        .map((label) => this.getDimensionLabel(label));

      const mapLikertTo04 = (v) => {
        if (v == null || Number.isNaN(Number(v))) return v;
        const n = Number(v);
        return n >= 1 && n <= 5 ? n - 1 : n;
      };
      const mapPlainBarPoint = (d) => {
        if (d == null) return d;
        const n = mapLikertTo04(d);
        if (typeof n !== 'number' || Number.isNaN(n)) return d;
        return n === 0 ? VISUAL_ZERO : n;
      };
      const mapErrorBarPoint = (d) => {
        if (d == null || typeof d !== 'object' || !('y' in d)) return d;
        const y = Number(d.y);
        const yMin = Number(d.yMin);
        const yMax = Number(d.yMax);
        const my = mapLikertTo04(y);
        const mMin = mapLikertTo04(yMin);
        const mMax = mapLikertTo04(yMax);
        const ny = typeof my === 'number' && my === 0 ? VISUAL_ZERO : my;
        return {
          ...d,
          y: ny,
          yMin: mMin,
          yMax: mMax,
        };
      };

      const shouldFloatLikert = this.useFloatingLikertBars;

      return {
        ...chartConfig.data,
        labels: translatedLabels,
        datasets: chartConfig.data.datasets.map((dataset) => ({
          ...dataset,
          data: shouldFloatLikert && Array.isArray(dataset.data)
            ? dataset.data.map((pt) => (
              pt != null && typeof pt === 'object' && 'yMin' in pt
                ? mapErrorBarPoint(pt)
                : mapPlainBarPoint(pt)
            ))
            : dataset.data,
          backgroundColor: this.normalizeColor(dataset.backgroundColor),
          borderColor: this.normalizeColor(dataset.borderColor),
        })),
      };
    },
  },
};
</script>

<style scoped>
/* Component-specific styles if needed */
</style>
